'use client';

/**
 * WebFooterCard — bottom-region panel for mode7.
 *
 * Mounted by the panels-as-cards primitive (region='bottom') in both
 * landing and /for-buyers presets. Single source for the deck-bottom
 * chrome regardless of which page is active.
 *
 * Hardcoded React for now — same posture as WebTopNavCard. Earns a
 * Pydantic content model + scaffold + prose YAML + BFF strategy when
 * the source Footer's full surface ports (legal links, social, contact,
 * sitemap, language switcher).
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useLayoutStore } from '@/lib/stores/layout-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';


interface FooterLink {
  label: string;
  /** explorerSelection value — null = landing. */
  selection: string | null;
}

const PRIMARY_LINKS: FooterLink[] = [
  { label: 'Home',             selection: null            },
  { label: 'For Buyers',       selection: 'for_buyers'    },
  { label: 'For Vendors',      selection: 'for_suppliers' },
  { label: 'Resources',        selection: 'resources'     },
  { label: 'Ask Klyde',        selection: 'reception'     },
];


export function WebFooterCard() {
  // Footer slots use the bundle that @card(typography=...) declares for
  // web_footer_canvas. Bundle map:
  //   sectionLabel → brand wordmark   (small bold)
  //   subtitle     → tagline / nav links / copyright (small muted)
  const T = useCardTypography();
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  const year = new Date().getFullYear();

  return (
    <footer
      className={cn(
        'web-surface w-full border-t border-border/40 bg-background',
        'py-8 px-6',
      )}
    >
      <div className="container mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="flex flex-col gap-1">
          <span className={cn(T.sectionLabel, 'text-foreground')}>
            Klyde
          </span>
          <span className={T.subtitle}>
            The Quoting Operating System for Procurement.
          </span>
        </div>

        <nav className="flex flex-wrap gap-x-6 gap-y-2">
          {PRIMARY_LINKS.map((link) => (
            <button
              key={link.label}
              type="button"
              onClick={() => setExplorerSelection(link.selection)}
              className={cn(T.subtitle, 'hover:text-foreground transition-colors')}
            >
              {link.label}
            </button>
          ))}
          <a
            href="mailto:hello@klyde.com.au"
            className={cn(T.subtitle, 'hover:text-foreground transition-colors')}
          >
            hello@klyde.com.au
          </a>
        </nav>

        <span className={T.subtitle}>
          © {year} Klyde. Klyde is a registered trademark.
        </span>
      </div>
    </footer>
  );
}
